import {
  Form,
  DatePicker,
  Radio,
  Select,
  Input,
  InputNumber,
  Row,
  Col,
  Divider,
} from "antd";
import { MapPin } from "lucide-react";
import dayjs from "dayjs";
import { textInputValidator } from "commons/helper";
import {
  STATE_OPTIONS,
  getCityOptions,
} from "pages/Events/commons/events.helpers";
// import MediaUpload from "Components/MediaUpload/MediaUpload";
import VenueSearch from "Components/VenueSearch/VenueSearch";

function VenueSelect({ value, onChange, locations, loading, disabled }) {
  return (
    <Select
      showSearch
      value={value || undefined}
      onChange={onChange}
      loading={loading}
      disabled={disabled}
      placeholder="Search and select a venue…"
      filterOption={(input, option) =>
        `${option.location_name} ${option.address}`
          .toLowerCase()
          .includes(input.toLowerCase())
      }
      notFoundContent='No existing venues found. Switch to "Add New Venue".'
      style={{ width: "100%" }}
      options={locations.map((loc) => ({
        value: loc.id,
        location_name: loc.location_name,
        address: [loc.address_line_1, loc.city, loc.state]
          .filter(Boolean)
          .join(", "),
        label: loc.location_name,
      }))}
      optionRender={(option) => (
        <div>
          <div style={{ fontWeight: 500 }}>{option.data.location_name}</div>
          <div
            style={{
              fontSize: 12,
              color: "var(--showup-text-secondary, #888)",
            }}
          >
            {option.data.address}
          </div>
        </div>
      )}
    />
  );
}

// function LocationAmenitiesSelect({ value = [], onChange, amenities, loading }) {
//   const codes = value.map(
//     (a) =>
//       amenities.find((m) => m.amenity_name === a.name)?.amenity_code ?? a.name
//   );
//   const handleChange = (selectedCodes) => {
//     onChange(
//       selectedCodes.map((code) => {
//         const found = amenities.find((a) => a.amenity_code === code);
//         return {
//           name: found?.amenity_name ?? code,
//           icon_url: getPrimaryMediaUrl(found?.media) ?? "",
//         };
//       })
//     );
//   };
//   return (
//     <Select
//       mode="multiple"
//       loading={loading}
//       placeholder="Select amenities available at this venue"
//       value={codes}
//       onChange={handleChange}
//       options={amenities.map((a) => ({
//         value: a.amenity_code,
//         label: (
//           <Row align="middle" justify="space-between">
//             <Col>{a.amenity_name}</Col>
//             <Col>
//               <Image
//                 width={20}
//                 height={20}
//                 preview={false}
//                 src={getPrimaryMediaUrl(a.media)}
//                 alt={a.amenity_name}
//               />
//             </Col>
//           </Row>
//         ),
//       }))}
//     />
//   );
// }

function Step2Location({
  form,
  locations,
  // amenities,
  loadingData,
  isPublished,
}) {
  const locationMode = Form.useWatch("location_mode", form);
  const selectedState = Form.useWatch(["location", "state"], form);

  const handleStateChange = (value) => {
    form.setFieldsValue({
      location: {
        ...form.getFieldValue("location"),
        state: value,
        city: undefined,
      },
    });
  };

  const handleVenueAutoFill = (fields) => {
    form.setFieldsValue({
      location: {
        // ...form.getFieldValue("location"),
        name: fields.name,
        address_line_1: fields.address_line_1,
        address_line_2: fields.address_line_2,
        ...(fields.area_code && { area_code: fields.area_code }),
        city: fields.city,
        state: fields.state,
        postal_code: fields.postal_code,
        country_code: fields.country_code,
        geo_location: {
          lat: fields.geo_lat,
          lng: fields.geo_lng,
        },
      },
    });
  };

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold">Date & Location</h2>

      {isPublished && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          You can update the start and end dates, but the venue cannot be
          changed after publishing.
        </div>
      )}

      {/* Dates */}
      <Row gutter={[24, 0]}>
        <Col xs={24} md={12}>
          <Form.Item
            name="start_date_time"
            label="Start Date & Time"
            rules={[{ required: true, message: "Start date is required" }]}
          >
            <DatePicker
              showTime
              disabledDate={(d, info) =>
                info?.type === "date" ? d && d < dayjs().startOf("day") : false
              }
              disabledTime={(d) => {
                if (!d || !d.isSame(dayjs(), "day")) return {};
                const now = dayjs();
                return {
                  disabledHours: () =>
                    Array.from({ length: now.hour() }, (_, i) => i),
                  disabledMinutes: (h) =>
                    h === now.hour()
                      ? Array.from({ length: now.minute() + 1 }, (_, i) => i)
                      : [],
                };
              }}
              style={{ width: "100%" }}
              format="MMM D, YYYY HH:mm"
              onChange={() => form.validateFields(["end_date_time"])}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item
            name="end_date_time"
            label="End Date & Time"
            rules={[
              {
                validator(_, v) {
                  const start = form.getFieldValue("start_date_time");
                  if (start && v.isBefore(dayjs(start).add(1, "hour")))
                    return Promise.reject(
                      new Error(
                        "End date must be at least 1 hour after the start date."
                      )
                    );
                  return Promise.resolve();
                },
              },
            ]}
          >
            <DatePicker
              showTime
              disabledDate={(d, info) => {
                if (info?.type !== "date") return false;
                const start = form.getFieldValue("start_date_time");
                if (start) return d && d < dayjs(start).startOf("day");
                return d && d < dayjs().startOf("day");
              }}
              disabledTime={(d) => {
                const start = form.getFieldValue("start_date_time");
                if (!start || !d) return {};
                const earliest = dayjs(start).add(1, "hour");
                if (!d.isSame(earliest, "day")) return {};
                const minHour = earliest.hour();
                const minMinute = earliest.minute();
                return {
                  disabledHours: () =>
                    Array.from({ length: minHour }, (_, i) => i),
                  disabledMinutes: (h) =>
                    h === minHour
                      ? Array.from({ length: minMinute }, (_, i) => i)
                      : [],
                };
              }}
              style={{ width: "100%" }}
              format="MMM D, YYYY HH:mm"
            />
          </Form.Item>
        </Col>
      </Row>

      <Divider orientation="left" plain>
        <span className="text-sm font-semibold flex items-center gap-1">
          <MapPin className="h-4 w-4" /> Venue
        </span>
      </Divider>

      <Form.Item
        name="location_mode"
        label="Venue"
        rules={[{ required: true, message: "Please select a venue option." }]}
      >
        <Radio.Group
          buttonStyle="solid"
          disabled={isPublished}
          onChange={(e) => {
            if (e.target.value === "new") {
              form.setFieldsValue({
                location_id: undefined,
                location: undefined,
              });
            } else {
              form.setFieldsValue({ location: undefined });
            }
          }}
        >
          <Radio.Button value="existing">Use Existing Venue</Radio.Button>
          <Radio.Button value="new">Add New Venue</Radio.Button>
        </Radio.Group>
      </Form.Item>

      {/* ── Select existing venue ── */}
      {locationMode === "existing" && (
        <Form.Item
          name="location_id"
          rules={[
            {
              validator: async (_, value) => {
                if (!value)
                  return Promise.reject(new Error("Please select a venue"));
                return Promise.resolve();
              },
            },
          ]}
        >
          <VenueSelect
            locations={locations}
            loading={loadingData}
            disabled={isPublished}
          />
        </Form.Item>
      )}

      {/* ── Add new venue ── */}
      {!isPublished && locationMode === "new" && (
        <div className="space-y-4 rounded-xl border p-5 bg-muted/20">
          <h3 className="text-sm font-semibold">New Venue</h3>

          <Form.Item label="Search Venue">
            <VenueSearch onChange={handleVenueAutoFill} />
          </Form.Item>

          <Row gutter={[16, 0]}>
            <Col xs={24} md={12}>
              <Form.Item
                name={["location", "name"]}
                label="Venue Name"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: "Venue name is required",
                  },
                  { validator: textInputValidator },
                ]}
              >
                <Input placeholder="e.g. NSCI Dome" maxLength={100} showCount />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name={["location", "address_line_1"]}
                label="Address Line 1"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: "Address is required",
                  },
                  { validator: textInputValidator },
                ]}
              >
                <Input placeholder="Street address" maxLength={100} showCount />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 0]}>
            <Col xs={24} sm={6}>
              <Form.Item
                name={["location", "state"]}
                label="State"
                rules={[
                  {
                    required: true,
                    message: "State is required",
                  },
                ]}
              >
                <Select
                  showSearch
                  placeholder="Select state"
                  options={STATE_OPTIONS}
                  onChange={handleStateChange}
                  filterOption={(input, option) =>
                    option.label.toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item
                name={["location", "city"]}
                label="City"
                rules={[
                  {
                    required: true,
                    message: "City is required",
                  },
                ]}
              >
                <Select
                  showSearch
                  placeholder="Select city"
                  options={getCityOptions(selectedState)}
                  filterOption={(input, option) =>
                    option.label.toLowerCase().includes(input.toLowerCase())
                  }
                  disabled={!selectedState}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item
                name={["location", "postal_code"]}
                label="Postal Code"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: "Postal code is required",
                  },
                  {
                    pattern: /^[0-9]+$/,
                    message: "Digits only",
                  },
                ]}
              >
                <Input maxLength={6} minLength={6} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item
                name={["location", "country_code"]}
                label="Country Code"
                initialValue="IN"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: "Country code is required",
                  },
                  { len: 2, message: "Must be a 2-letter code (e.g. IN)" },
                ]}
              >
                <Input maxLength={2} placeholder="IN" disabled />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={[16, 0]}>
            <Col xs={24} sm={12}>
              <Form.Item
                name={["location", "geo_location", "lat"]}
                label="Latitude"
                rules={[
                  { required: true, message: "Latitude is required" },
                  {
                    type: "number",
                    min: -90,
                    max: 90,
                    message: "Must be between -90 and 90",
                  },
                ]}
              >
                <InputNumber
                  disabled
                  style={{ width: "100%" }}
                  placeholder="Auto-filled from map"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name={["location", "geo_location", "lng"]}
                label="Longitude"
                rules={[
                  { required: true, message: "Longitude is required" },
                  {
                    type: "number",
                    min: -180,
                    max: 180,
                    message: "Must be between -180 and 180",
                  },
                ]}
              >
                <InputNumber
                  disabled
                  style={{ width: "100%" }}
                  placeholder="Auto-filled from map"
                />
              </Form.Item>
            </Col>
          </Row>

          {/* <Form.Item name={["location", "amenities"]} label="Venue Amenities">
            <LocationAmenitiesSelect
              amenities={amenities}
              loading={loadingData}
            />
          </Form.Item>

          <Form.Item name={["location", "media_url"]} label="Venue Photo">
            <MediaUpload />
          </Form.Item> */}
        </div>
      )}
    </div>
  );
}

export default Step2Location;
